import { lazy, Suspense, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChartStore } from '@/store/chartStore';
import { useAuthStore } from '@/store/authStore';
import { Modal } from '@/components/ui/Modal';
import { SearchIcon, XIcon, LockIcon, StarIcon, StarFilledIcon } from '@/components/icons';
import type { Chart } from 'klinecharts';
import { trackIndicatorAction, trackUpgradeModalOpen } from '@/services/gtag';
import { INDICATORS, INDICATOR_DEFAULT_COLORS } from '@/components/Chart/indicatorDefs';
import { EpsLineConfigModal } from '@/components/Chart/EpsLineConfigModal';
import { CupHandleConfigModal } from '@/components/Chart/CupHandleConfigModal';
import { WilliamOneilSellConfigModal } from '@/components/Chart/WilliamOneilSellConfigModal';
import { ForeignHoldingConfigModal } from '@/components/Chart/ForeignHoldingConfigModal';
import type { IndicatorDef } from '@/components/Chart/indicatorDefs';
import { ensureBreadthData } from '@/store/breadthStore';
export type { IndicatorDef } from '@/components/Chart/indicatorDefs';

// Both pull in KaTeX (~70KB) for formula rendering — split out so it's only
// fetched when a user actually opens one of these config modals.
const IndicatorConfigModal = lazy(() =>
  import('@/components/Chart/IndicatorConfigModal').then((m) => ({ default: m.IndicatorConfigModal })),
);
const RSConfigModal = lazy(() =>
  import('@/components/Chart/RSConfigModal').then((m) => ({ default: m.RSConfigModal })),
);

type ChartV9 = Chart & {
  createIndicator: (name: string, isStack: boolean, paneOptions?: { id?: string; height?: number; minHeight?: number }) => string;
  removeIndicator: (paneId: string, name?: string) => void;
  overrideIndicator: (override: { name: string; calcParams?: number[]; styles?: Record<string, unknown> }, paneId?: string) => void;
};

type Tab = 'all' | 'general' | 'advanced';

interface Props {
  onClose: () => void;
}

export function IndicatorModal({ onClose }: Props) {
  const chart           = useChartStore((s) => s.chart) as ChartV9 | null;
  const paneMap         = useChartStore((s) => s.indicatorPaneMap);
  const setPaneMap      = useChartStore((s) => s.setIndicatorPaneMap);
  const addActiveIndicator    = useChartStore((s) => s.addActiveIndicator);
  const removeActiveIndicator = useChartStore((s) => s.removeActiveIndicator);
  const setIndicatorParam  = useChartStore((s) => s.setIndicatorParam);
  const clearIndicatorParam = useChartStore((s) => s.clearIndicatorParam);
  const indicatorParams = useChartStore((s) => s.indicatorParams);
  const indicatorColors = useChartStore((s) => s.indicatorColors);
  const setIndicatorColors  = useChartStore((s) => s.setIndicatorColors);
  const clearIndicatorColors = useChartStore((s) => s.clearIndicatorColors);
  const indicatorScanConfigs    = useChartStore((s) => s.indicatorScanConfigs);
  const setIndicatorScanConfigs = useChartStore((s) => s.setIndicatorScanConfigs);
  const volumeLabelEnabled      = useChartStore((s) => s.volumeLabelEnabled);
  const setVolumeLabelEnabled   = useChartStore((s) => s.setVolumeLabelEnabled);
  const crossVolumeEnabled      = useChartStore((s) => s.crossVolumeEnabled);
  const setCrossVolumeEnabled   = useChartStore((s) => s.setCrossVolumeEnabled);
  const rsiZonesEnabled         = useChartStore((s) => s.rsiZonesEnabled);
  const setRsiZonesEnabled      = useChartStore((s) => s.setRsiZonesEnabled);
  const rsiDivergenceEnabled    = useChartStore((s) => s.rsiDivergenceEnabled);
  const setRsiDivergenceEnabled = useChartStore((s) => s.setRsiDivergenceEnabled);
  const rsiDivergencePeriod            = useChartStore((s) => s.rsiDivergencePeriod);
  const setRsiDivergencePeriod         = useChartStore((s) => s.setRsiDivergencePeriod);
  const rsiDivergencePivotLookback     = useChartStore((s) => s.rsiDivergencePivotLookback);
  const setRsiDivergencePivotLookback  = useChartStore((s) => s.setRsiDivergencePivotLookback);
  const user            = useAuthStore((s) => s.user);
  const openUpgradeModal = useAuthStore((s) => s.openUpgradeModal);
  const navigate        = useNavigate();

  const favoriteIndicatorNames   = useChartStore((s) => s.favoriteIndicatorNames);
  const addFavoriteIndicator     = useChartStore((s) => s.addFavoriteIndicator);
  const removeFavoriteIndicator  = useChartStore((s) => s.removeFavoriteIndicator);

  const [query,       setQuery]       = useState('');
  const [tab,         setTab]         = useState<Tab>('all');
  const [configuring, setConfiguring] = useState<IndicatorDef | null>(null);
  const [rsConfigOpen, setRsConfigOpen] = useState(false);
  const [epsLineConfigOpen, setEpsLineConfigOpen] = useState(false);
  const [cupHandleConfigOpen, setCupHandleConfigOpen] = useState(false);
  const [oneilSellConfigOpen, setOneilSellConfigOpen] = useState(false);
  const [foreignHoldingConfigOpen, setForeignHoldingConfigOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isGuest = !user;
  // Advanced indicators are a Pro/Business perk — basic-plan users (and guests)
  // don't get them, not just guests. See UpgradeModal's PRO_FEATURES list.
  const hasAdvancedAccess = user?.plan === 'pro' || user?.plan === 'business';

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Closes the innermost open sub-modal first; only closes the catalog
  // itself once nothing else is open. Shared by Escape (via Modal), the
  // header close button, and backdrop click, so all three stay in sync.
  function handleClose() {
    if (configuring || rsConfigOpen || epsLineConfigOpen || cupHandleConfigOpen || oneilSellConfigOpen || foreignHoldingConfigOpen) {
      setConfiguring(null);
      setRsConfigOpen(false);
      setEpsLineConfigOpen(false);
      setCupHandleConfigOpen(false);
      setOneilSellConfigOpen(false);
      setForeignHoldingConfigOpen(false);
    } else {
      onClose();
    }
  }

  const rsLineConfigs = useChartStore((s) => s.rsLineConfigs);

  const q = query.toLowerCase();

  // Does the search query match the RS line entry?
  const rsLineMatches = !q ||
    'rs'.includes(q) ||
    'rs line'.includes(q) ||
    'rs rating'.includes(q) ||
    'relative strength'.includes(q);

  const visibleIndicators = INDICATORS.filter((ind) => {
    if (tab === 'general' && ind.category !== 'general') return false;
    if (tab === 'advanced' && ind.category !== 'advanced') return false;
    if (!q) return true;
    return ind.name.toLowerCase().includes(q) || ind.longName.toLowerCase().includes(q);
  });

  const generalCount  = INDICATORS.filter((i) => i.category === 'general').length;
  const advancedCount = INDICATORS.filter((i) => i.category === 'advanced').length;
  const activeCount   = paneMap.size;

  // RS line is "active" if at least one config is visible
  const rsActive = rsLineConfigs.some((c) => c.visible);

  // ── Handlers passed down to IndicatorConfigModal ─────────────────────────

  function buildLineStyles(colors: string[]) {
    // Must include ALL line style fields — KlineCharts' merge check accesses
    // dashedValue[0] without a null guard (index.esm.js ~line 8023), so a
    // partial { color } object causes "Cannot read properties of undefined
    // (reading '0')" when the line-segment merge loop compares adjacent segments.
    return colors.map((color) => ({
      style: 'solid' as const,
      smooth: false,
      size: 1,
      dashedValue: [2, 2],
      color,
    }));
  }

  // Returns the correct styles object for overrideIndicator.  Bar-type indicators
  // (e.g. VOL) use `bars[].upColor / downColor`; all others use `lines[]`.
  function buildOverrideStyles(ind: IndicatorDef, colors: string[]): Record<string, unknown> {
    if (ind.name === 'VOL') {
      return {
        bars: [{
          style: 'fill',
          upColor:       colors[0],
          downColor:     colors[1] ?? colors[0],
          noChangeColor: colors[0],
        }],
      };
    }
    return { lines: buildLineStyles(colors) };
  }

  function handleAdd(ind: IndicatorDef, params: number[], colors: string[]) {
    if (!chart) return;

    // Close the config panel first so React flushes its render before chart ops.
    setConfiguring(null);

    // Defer chart mutations to the next macrotask (after React's render is flushed).
    // For breadth indicators, fetch data first then create the indicator.
    const doAdd = async () => {
      if (ind.requiresBreadthData) {
        await ensureBreadthData();
      }

      const liveChart = useChartStore.getState().chart as ChartV9 | null;
      if (!liveChart) return;

      const opts = ind.paneId ? { id: ind.paneId } : { height: ind.paneHeight ?? 100, minHeight: 60 };

      // Pass name as a plain STRING — never embed styles inside createIndicator.
      // When KlineCharts receives an IndicatorCreate object that includes styles, it
      // fires an internal overrideIndicator before the first calc() has run, so
      // drawImp iterates result[] over an empty array and crashes at undefined[0].
      // The safe pattern (same as template load): create with string, then override.
      const returnedId = liveChart.createIndicator(ind.name, !!ind.paneId, opts);
      const resolvedId = ind.paneId ?? returnedId ?? ind.name;

      // When re-adding VOL, also re-create its companion stacked indicators.
      if (ind.name === 'VOL') {
        try { liveChart.createIndicator('VOL_LABELS', true, { id: 'vol_pane' }); } catch { /* ignore */ }
        try { liveChart.createIndicator('CROSS_VOL', true, { id: 'vol_pane' }); } catch { /* ignore */ }
      }

      // When re-adding RSI, also re-create its companion stacked indicators
      // (overbought/oversold zone shading + divergence overlay).
      if (ind.name === 'RSI') {
        try { liveChart.createIndicator('RSI_ZONES', true, { id: resolvedId }); } catch { /* ignore */ }
        try {
          liveChart.createIndicator('RSI_DIVERGENCE', true, { id: resolvedId });
          // Apply the user's persisted divergence params (template default is [14, 2]).
          liveChart.overrideIndicator({
            name: 'RSI_DIVERGENCE',
            calcParams: [
              useChartStore.getState().rsiDivergencePeriod,
              useChartStore.getState().rsiDivergencePivotLookback,
            ],
          } as never, resolvedId);
        } catch { /* ignore */ }
      }

      // Apply calcParams + line-colour styles via overrideIndicator.  By this
      // point KlineCharts has already run the first calc(), so the result array
      // is fully populated and drawImp won't crash.
      const styles: Record<string, unknown> = buildOverrideStyles(ind, colors);
      if (ind.paneId === 'candle_pane') styles.tooltip = { showRule: 'none' };

      const override: Record<string, unknown> = {
        name: ind.name,
        styles,
        // Enable compact Y-axis formatting (K/M/B) for sub-pane indicators.
        // candle_pane overlays (MA, EMA, BOLL, SAR) show price values — skip them.
        shouldFormatBigNumber: ind.paneId !== 'candle_pane',
      };
      if (params.length > 0) override.calcParams = params;
      liveChart.overrideIndicator(override as never, resolvedId || undefined);

      const currentMap = useChartStore.getState().indicatorPaneMap;
      setPaneMap(new Map([...currentMap, [ind.name, resolvedId]]));
      if (params.length > 0) setIndicatorParam(ind.name, params);
      setIndicatorColors(ind.name, colors);
      addActiveIndicator(ind.name);
      trackIndicatorAction('add', ind.name);
      void useChartStore.getState().rebalancePanes();
    };

    void doAdd();
  }

  function handleUpdate(ind: IndicatorDef, params: number[], colors: string[]) {
    if (!chart) return;
    const paneId = paneMap.get(ind.name);
    if (!paneId) return;

    const override: { name: string; calcParams?: number[]; styles?: Record<string, unknown> } = { name: ind.name };
    if (params.length > 0) override.calcParams = params;
    override.styles = buildOverrideStyles(ind, colors);
    chart.overrideIndicator(override, paneId);

    if (params.length > 0) setIndicatorParam(ind.name, params);
    setIndicatorColors(ind.name, colors);
    setConfiguring(null);
  }

  function handleRemove(ind: IndicatorDef) {
    if (!chart) return;
    const paneId = paneMap.get(ind.name);
    if (!paneId) return;
    chart.removeIndicator(paneId, ind.name);
    const next = new Map(paneMap);
    next.delete(ind.name);
    setPaneMap(next);
    clearIndicatorParam(ind.name);
    clearIndicatorColors(ind.name);
    removeActiveIndicator(ind.name);
    trackIndicatorAction('remove', ind.name);
    // When removing VOL, also remove its companion stacked indicators
    if (ind.name === 'VOL') {
      try { chart.removeIndicator(paneId, 'VOL_LABELS'); } catch { /* ignore */ }
      try { chart.removeIndicator(paneId, 'CROSS_VOL'); } catch { /* ignore */ }
      if (volumeLabelEnabled) setVolumeLabelEnabled(false);
    }
    // RSI's zone/divergence companions are removed along with RSI, but their
    // enabled flags are intentionally NOT reset — they're meant to feel like
    // an intrinsic part of RSI's default look, so re-adding RSI should
    // restore the user's last preference rather than reverting it.
    if (ind.name === 'RSI') {
      try { chart.removeIndicator(paneId, 'RSI_ZONES'); } catch { /* ignore */ }
      try { chart.removeIndicator(paneId, 'RSI_DIVERGENCE'); } catch { /* ignore */ }
    }
    if (ind.name === 'EPS_LINE') useChartStore.getState().setEpsLineEnabled(false);
    if (ind.name === 'CUP_HANDLE') useChartStore.getState().setCupHandleEnabled(false);
    if (ind.name === 'WILLIAM_ONEIL_SELL') useChartStore.getState().setWilliamOneilSellEnabled(false);
    setConfiguring(null);
    void useChartStore.getState().rebalancePanes();
  }

  const TABS: { id: Tab; label: string; count: number }[] = [
    { id: 'all',      label: 'All',      count: INDICATORS.length + 1 }, // +1 for RS Line
    { id: 'general',  label: 'General',  count: generalCount },
    { id: 'advanced', label: 'Advanced', count: advancedCount },
  ];

  return (
    <>
      {/* ── Main catalog modal ─────────────────────────────────────────── */}
      <Modal onClose={handleClose} size="xl" className="h-[min(90vh,720px)]">
        {/* Header — no bottom border; grouped with search + filter pills below
            it, which share one divider after the pills instead. */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-modal-text">Indicators</span>
            {activeCount > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-accent/15 text-accent font-medium">
                {activeCount} active
              </span>
            )}
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg text-modal-muted hover:text-modal-text hover:bg-modal-hover transition-colors"
            aria-label="Close"
          >
            <XIcon size={15} />
          </button>
        </div>

        {/* Search bar */}
        <div className="px-4 pb-3 shrink-0">
            <div className="flex items-center gap-2.5 px-3.5 h-11 bg-bg border border-border rounded-xl focus-within:border-accent transition-colors">
              <SearchIcon size={15} className="text-modal-muted shrink-0" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search indicators…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="flex-1 bg-transparent text-modal-text text-base outline-none placeholder:text-modal-muted"
                autoComplete="off"
                spellCheck={false}
              />
              {query && (
                <button
                  onClick={() => { setQuery(''); inputRef.current?.focus(); }}
                  className="p-0.5 rounded-full text-modal-muted hover:text-modal-text transition-colors"
                  aria-label="Clear search"
                >
                  <XIcon size={13} />
                </button>
              )}
            </div>
          </div>

          {/* Filter pills */}
          <div className="flex items-center gap-1.5 px-4 pb-3 shrink-0">
            {TABS.map(({ id, label, count }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  tab === id
                    ? 'bg-accent text-white'
                    : 'bg-modal-hover text-modal-muted hover:text-modal-text'
                }`}
              >
                {label}
                <span className={`ml-1.5 ${tab === id ? 'text-white/70' : 'text-modal-muted'}`}>
                  {count}
                </span>
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className="h-px bg-border shrink-0" />

          {/* Indicator list */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {visibleIndicators.length === 0 && !(rsLineMatches && tab !== 'general') ? (
              <div className="flex flex-col items-center justify-center h-full gap-1.5">
                <span className="text-sm text-modal-text">No results for "{query}"</span>
                <span className="text-xs text-modal-muted">Try a different name</span>
              </div>
            ) : (
              <>
                {visibleIndicators.map((ind) => {
                  const isLocked = !hasAdvancedAccess && ind.category === 'advanced';
                  const active   = paneMap.has(ind.name);
                  const isFav    = favoriteIndicatorNames.includes(ind.name);
                  return (
                    <div
                      key={ind.name}
                      className={`w-full text-left px-5 flex items-center gap-3 py-3.5 transition-colors hover:bg-modal-hover ${isLocked ? 'opacity-50' : ''}`}
                    >
                      {/* Clickable area */}
                      <button
                        onClick={() => {
                          if (isLocked) {
                            if (isGuest) { onClose(); navigate('/login'); return; }
                            trackUpgradeModalOpen('advanced_indicator');
                            openUpgradeModal();
                            return;
                          }
                          if (ind.name === 'EPS_LINE') {
                            setEpsLineConfigOpen(true);
                            return;
                          }
                          if (ind.name === 'RS_LINE') {
                            setRsConfigOpen(true);
                            return;
                          }
                          if (ind.name === 'CUP_HANDLE') {
                            setCupHandleConfigOpen(true);
                            return;
                          }
                          if (ind.name === 'WILLIAM_ONEIL_SELL') {
                            setOneilSellConfigOpen(true);
                            return;
                          }
                          if (ind.name === 'FOREIGN_HOLDING') {
                            setForeignHoldingConfigOpen(true);
                            return;
                          }
                          setConfiguring(ind);
                        }}
                        className="flex items-center gap-3 flex-1 text-left"
                      >
                        {/* Name */}
                        <span className="font-bold text-sm text-modal-text w-40 shrink-0">{ind.name}</span>

                        {/* Long name */}
                        <span className="text-sm text-modal-muted flex-1">{ind.longName}</span>
                      </button>

                      {/* Favorite star */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isFav) {
                            removeFavoriteIndicator(ind.name);
                          } else {
                            addFavoriteIndicator(ind.name);
                          }
                        }}
                        className={`shrink-0 transition-colors ${isFav ? 'text-yellow-400' : 'text-modal-muted hover:text-yellow-400'}`}
                        title={isFav ? 'Remove from toolbar' : 'Add to toolbar'}
                      >
                        {isFav ? <StarFilledIcon size={14} /> : <StarIcon size={14} />}
                      </button>

                      {/* Lock icon for guests/basic-plan users on advanced indicators */}
                      {isLocked ? (
                        <LockIcon size={14} className="text-modal-muted shrink-0" />
                      ) : active ? (
                        <span className="flex items-center gap-1 text-xs text-accent shrink-0 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                          Active
                        </span>
                      ) : null}
                    </div>
                  );
                })}


              </>
            )}
          </div>

        {/* Footer */}
        <div className="h-px bg-border shrink-0" />
        <div className="px-5 py-3 text-center">
          <span className="text-xs text-modal-muted">
            {visibleIndicators.length + (rsLineMatches && tab !== 'general' ? 1 : 0)} indicator{visibleIndicators.length !== 1 ? 's' : ''} · {activeCount} active
          </span>
        </div>
      </Modal>

      {/* ── Per-indicator config modal ─────────────────────────────────── */}
      {configuring && (
        <Suspense fallback={null}>
        <IndicatorConfigModal
          ind={configuring}
          isActive={paneMap.has(configuring.name)}
          currentParams={indicatorParams[configuring.name] ?? configuring.defaultParams ?? []}
          currentColors={indicatorColors[configuring.name] ?? INDICATOR_DEFAULT_COLORS[configuring.name] ?? []}
          currentScanConfigs={indicatorScanConfigs[configuring.name] ?? []}
          volumeLabelsEnabled={volumeLabelEnabled}
          crossVolumeEnabled={crossVolumeEnabled}
          rsiZonesEnabled={rsiZonesEnabled}
          rsiDivergenceEnabled={rsiDivergenceEnabled}
          rsiDivergencePeriod={rsiDivergencePeriod}
          rsiDivergencePivotLookback={rsiDivergencePivotLookback}
          onAdd={(params, colors) => handleAdd(configuring, params, colors)}
          onUpdate={(params, colors) => handleUpdate(configuring, params, colors)}
          onPreviewUpdate={(params, colors) => handleUpdate(configuring, params, colors)}
          onRemove={() => handleRemove(configuring)}
          onClose={() => setConfiguring(null)}
          onScanUpdate={(configs) => {
            setIndicatorScanConfigs(configuring.name, configs);
            const c = chart as unknown as { overrideIndicator: (o: Record<string, unknown>, paneId: string) => void };
            try { c?.overrideIndicator({ name: 'VOL_LABELS' }, 'vol_pane'); } catch { /* ignore */ }
          }}
          onVolumeLabelsToggle={(enabled) => {
            setVolumeLabelEnabled(enabled);
            const c = chart as unknown as { overrideIndicator: (o: Record<string, unknown>, paneId: string) => void };
            try { c?.overrideIndicator({ name: 'VOL_LABELS' }, 'vol_pane'); } catch { /* ignore */ }
          }}
          onCrossVolumeToggle={(enabled) => {
            setCrossVolumeEnabled(enabled);
            const c = chart as unknown as { overrideIndicator: (o: Record<string, unknown>, paneId: string) => void };
            try { c?.overrideIndicator({ name: 'VOL' }, 'vol_pane'); } catch { /* ignore */ }
            try { c?.overrideIndicator({ name: 'VOL_LABELS' }, 'vol_pane'); } catch { /* ignore */ }
            try { c?.overrideIndicator({ name: 'CROSS_VOL' }, 'vol_pane'); } catch { /* ignore */ }
          }}
          onRsiZonesToggle={(enabled) => {
            setRsiZonesEnabled(enabled);
            const paneId = paneMap.get('RSI');
            const c = chart as unknown as { overrideIndicator: (o: Record<string, unknown>, paneId: string) => void };
            if (paneId) { try { c?.overrideIndicator({ name: 'RSI_ZONES', styles: {} }, paneId); } catch { /* ignore */ } }
          }}
          onRsiDivergenceToggle={(enabled) => {
            setRsiDivergenceEnabled(enabled);
            const paneId = paneMap.get('RSI');
            const c = chart as unknown as { overrideIndicator: (o: Record<string, unknown>, paneId: string) => void };
            if (paneId) { try { c?.overrideIndicator({ name: 'RSI_DIVERGENCE', styles: {} }, paneId); } catch { /* ignore */ } }
          }}
          onRsiDivergenceParamsChange={(period, pivotLookback) => {
            setRsiDivergencePeriod(period);
            setRsiDivergencePivotLookback(pivotLookback);
            const paneId = paneMap.get('RSI');
            const c = chart as unknown as { overrideIndicator: (o: Record<string, unknown>, paneId: string) => void };
            if (paneId) {
              try { c?.overrideIndicator({ name: 'RSI_DIVERGENCE', calcParams: [period, pivotLookback] }, paneId); } catch { /* ignore */ }
            }
          }}
        />
        </Suspense>
      )}

      {/* ── RS Line config modal ───────────────────────────────────────── */}
      {rsConfigOpen && (
        <Suspense fallback={null}>
          <RSConfigModal isActive={rsActive} onClose={() => setRsConfigOpen(false)} />
        </Suspense>
      )}

      {/* ── EPS Line (secondary axis) config modal ─────────────────────── */}
      {epsLineConfigOpen && (
        <EpsLineConfigModal
          isActive={paneMap.has('EPS_LINE')}
          onClose={() => setEpsLineConfigOpen(false)}
          onAdd={() => {
            const liveChart = useChartStore.getState().chart as ChartV9 | null;
            if (liveChart && !paneMap.has('EPS_LINE')) {
              try {
                liveChart.createIndicator('EPS_LINE', true, { id: 'candle_pane' });
                const currentMap = useChartStore.getState().indicatorPaneMap;
                useChartStore.getState().setIndicatorPaneMap(new Map([...currentMap, ['EPS_LINE', 'candle_pane']]));
                useChartStore.getState().setEpsLineEnabled(true);
                useChartStore.getState().addActiveIndicator('EPS_LINE');
              } catch { /* ignore */ }
            }
          }}
        />
      )}

      {/* ── Cup & Handle config modal ────────────────────────────────────── */}
      {cupHandleConfigOpen && (
        <CupHandleConfigModal
          isActive={paneMap.has('CUP_HANDLE')}
          onClose={() => setCupHandleConfigOpen(false)}
          onAdd={() => {
            const liveChart = useChartStore.getState().chart as ChartV9 | null;
            if (liveChart && !paneMap.has('CUP_HANDLE')) {
              try {
                liveChart.createIndicator('CUP_HANDLE', true, { id: 'candle_pane' });
                const currentMap = useChartStore.getState().indicatorPaneMap;
                useChartStore.getState().setIndicatorPaneMap(new Map([...currentMap, ['CUP_HANDLE', 'candle_pane']]));
                useChartStore.getState().setCupHandleEnabled(true);
                useChartStore.getState().addActiveIndicator('CUP_HANDLE');
              } catch { /* ignore */ }
            }
          }}
        />
      )}

      {/* ── William O'Neil Sell Signals config modal ─────────────────────── */}
      {oneilSellConfigOpen && (
        <WilliamOneilSellConfigModal
          isActive={paneMap.has('WILLIAM_ONEIL_SELL')}
          onClose={() => setOneilSellConfigOpen(false)}
          onAdd={() => {
            const liveChart = useChartStore.getState().chart as ChartV9 | null;
            if (liveChart && !paneMap.has('WILLIAM_ONEIL_SELL')) {
              try {
                liveChart.createIndicator('WILLIAM_ONEIL_SELL', true, { id: 'candle_pane' });
                const currentMap = useChartStore.getState().indicatorPaneMap;
                useChartStore.getState().setIndicatorPaneMap(new Map([...currentMap, ['WILLIAM_ONEIL_SELL', 'candle_pane']]));
                useChartStore.getState().setWilliamOneilSellEnabled(true);
                useChartStore.getState().addActiveIndicator('WILLIAM_ONEIL_SELL');
              } catch { /* ignore */ }
            }
          }}
        />
      )}

      {/* ── Foreign Holding config modal ─────────────────────────────────── */}
      {foreignHoldingConfigOpen && (
        <ForeignHoldingConfigModal
          isActive={paneMap.has('FOREIGN_HOLDING')}
          onClose={() => setForeignHoldingConfigOpen(false)}
          onAdd={() => {
            const liveChart = useChartStore.getState().chart as ChartV9 | null;
            if (liveChart && !paneMap.has('FOREIGN_HOLDING')) {
              try {
                const returnedPaneId = liveChart.createIndicator('FOREIGN_HOLDING', false, { height: 120, minHeight: 80 });
                const resolvedId = returnedPaneId ?? 'FOREIGN_HOLDING';
                const currentMap = useChartStore.getState().indicatorPaneMap;
                useChartStore.getState().setIndicatorPaneMap(new Map([...currentMap, ['FOREIGN_HOLDING', resolvedId]]));
                useChartStore.getState().addActiveIndicator('FOREIGN_HOLDING');
                void useChartStore.getState().rebalancePanes();
              } catch { /* ignore */ }
            }
          }}
        />
      )}
    </>
  );
}
